<template>
  <div class="payments-page">
    <header class="page-header">
      <h1 class="page-title">Payments</h1>
      <p class="page-subtitle">
        Connect Stripe so this organization can accept course payments and receive payouts to your bank account.
      </p>
    </header>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      Loading payment setup…
    </div>

    <template v-else>
      <!-- Platform Stripe not configured -->
      <section v-if="!accountStatus.has_connect_account" class="settings-card edge-card edge-neutral">
        <div class="card-header">
          <i class="fas fa-info-circle" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Platform prerequisite</h2>
            <p class="card-desc">
              Stripe must be configured once for the Praetorian platform before any org can onboard Connect.
            </p>
          </div>
        </div>
        <div class="card-body">
          <p class="info-text">
            Platform administrators add Stripe API keys to the deployment environment.
            See <code>STRIPE_ENV_TEMPLATE.txt</code> or register at
            <a href="https://dashboard.stripe.com/register" target="_blank" rel="noopener">stripe.com</a>.
          </p>
        </div>
      </section>

      <!-- Not started -->
      <section v-if="!accountStatus.has_connect_account" class="settings-card edge-card edge-neutral">
        <div class="card-header">
          <i class="fas fa-university" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Start accepting payments</h2>
            <p class="card-desc">
              Stripe Express onboarding verifies your organization, links a bank account, and enables paid courses.
            </p>
          </div>
        </div>

        <div class="card-body">
          <ol class="setup-steps">
            <li>
              <strong>Verify your organization</strong>
              <span>Business details required for payment regulations.</span>
            </li>
            <li>
              <strong>Connect your bank account</strong>
              <span>Payouts from course sales deposit here.</span>
            </li>
            <li>
              <strong>Enable paid courses</strong>
              <span>Publish priced offerings once charges are enabled.</span>
            </li>
          </ol>

          <div class="fee-block edge-card edge-warn">
            <h3 class="fee-title">Platform fee: 15%</h3>
            <p class="fee-desc">
              Praetorian retains 15% per transaction; your org receives 85% directly from Stripe.
            </p>
            <dl class="metrics-list fee-metrics">
              <div class="metric-row">
                <dt>Example sale</dt>
                <dd>$100.00 course</dd>
              </div>
              <div class="metric-row">
                <dt>You receive</dt>
                <dd>$85.00</dd>
              </div>
              <div class="metric-row">
                <dt>Platform fee</dt>
                <dd>$15.00</dd>
              </div>
            </dl>
          </div>

          <div class="card-actions">
            <button
              class="btn btn-primary"
              :disabled="starting"
              @click="startOnboarding"
            >
              <i class="fas fa-external-link-alt"></i>
              {{ starting ? 'Starting…' : 'Start Stripe setup' }}
            </button>
          </div>

          <p class="security-note">
            <i class="fas fa-lock"></i>
            Secure onboarding powered by Stripe. Your information is encrypted in transit.
          </p>
        </div>
      </section>

      <!-- Account exists -->
      <section
        v-else
        class="settings-card edge-card"
        :class="accountEdgeClass"
      >
        <div class="card-header">
          <i class="fas fa-check-circle" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Payment setup status</h2>
            <p class="card-desc">
              {{ statusSummary }}
            </p>
          </div>
        </div>

        <div class="card-body">
          <dl class="metrics-list">
            <div class="metric-row" :class="{ 'metric-row--ok': accountStatus.onboarding_complete }">
              <dt>Onboarding</dt>
              <dd>{{ accountStatus.onboarding_complete ? 'Complete' : 'In progress' }}</dd>
            </div>
            <div class="metric-row" :class="{ 'metric-row--ok': accountStatus.charges_enabled }">
              <dt>Charges</dt>
              <dd>{{ accountStatus.charges_enabled ? 'Enabled' : 'Not enabled' }}</dd>
            </div>
            <div class="metric-row" :class="{ 'metric-row--ok': accountStatus.payouts_enabled }">
              <dt>Payouts</dt>
              <dd>{{ accountStatus.payouts_enabled ? 'Enabled' : 'Not enabled' }}</dd>
            </div>
            <div class="metric-row">
              <dt>Platform fee</dt>
              <dd>{{ accountStatus.platform_fee_percentage }}%</dd>
            </div>
          </dl>

          <div
            v-if="accountStatus.requirements?.currently_due?.length"
            class="usage-alert warning"
          >
            <i class="fas fa-exclamation-triangle"></i>
            <div>
              <strong>Action required</strong>
              <p>Complete these items in Stripe to enable payment processing:</p>
              <ul class="req-list">
                <li v-for="(req, index) in accountStatus.requirements.currently_due" :key="index">
                  {{ formatRequirement(req) }}
                </li>
              </ul>
            </div>
          </div>

          <div
            v-if="isFullyConfigured"
            class="usage-alert ok"
          >
            <i class="fas fa-check-circle"></i>
            <span>
              Payment processing is fully configured. You can create paid courses and accept payments.
            </span>
          </div>

          <div class="card-actions">
            <button
              v-if="!accountStatus.onboarding_complete"
              class="btn btn-primary"
              :disabled="continuing"
              @click="continueOnboarding"
            >
              <i class="fas fa-external-link-alt"></i>
              {{ continuing ? 'Opening…' : 'Continue setup' }}
            </button>
            <button
              v-else
              class="btn btn-secondary"
              @click="manageAccount"
            >
              <i class="fas fa-cog"></i>
              Manage in Stripe
            </button>
          </div>
        </div>
      </section>

      <!-- Help -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-question-circle" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Need help?</h2>
            <p class="card-desc">Questions about Connect onboarding, payouts, or course pricing.</p>
          </div>
        </div>
        <div class="card-body help-grid">
          <div class="help-item">
            <h3 class="help-label">Support</h3>
            <p>Reach our team for setup or payout issues.</p>
            <a href="mailto:support@praetorianai.com" class="text-link">support@praetorianai.com</a>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'ConnectOnboarding',

  data() {
    return {
      loading: true,
      accountStatus: {
        has_connect_account: false,
      },
      starting: false,
      continuing: false,
    };
  },

  computed: {
    isFullyConfigured() {
      return this.accountStatus.onboarding_complete
        && this.accountStatus.charges_enabled
        && this.accountStatus.payouts_enabled;
    },
    accountEdgeClass() {
      if (this.isFullyConfigured) return 'edge-ok';
      if (this.accountStatus.requirements?.currently_due?.length) return 'edge-warn';
      return 'edge-neutral';
    },
    statusSummary() {
      if (this.isFullyConfigured) {
        return 'Stripe Connect is active — charges and payouts are enabled.';
      }
      if (this.accountStatus.onboarding_complete) {
        return 'Onboarding submitted — finish any remaining Stripe requirements.';
      }
      return 'Finish Stripe Express onboarding to accept course payments.';
    },
  },

  async mounted() {
    await this.checkAccountStatus();

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      this.$toast?.success('Account setup updated successfully!');
      await this.checkAccountStatus();
      window.history.replaceState({}, '', '/settings/payments');
    }
  },

  methods: {
    async checkAccountStatus() {
      this.loading = true;
      try {
        const response = await api.get('/lms/connect/status/');
        this.accountStatus = response.data;
      } catch (error) {
        console.error('Error checking account status:', error);
        this.$toast?.error('Failed to load account status');
      } finally {
        this.loading = false;
      }
    },

    async startOnboarding() {
      this.starting = true;
      try {
        await api.post('/lms/connect/create_account/', {
          account_type: 'EXPRESS',
        });

        const currentUrl = window.location.origin;
        const response = await api.post('/lms/connect/onboarding_link/', {
          refresh_url: `${currentUrl}/settings/payments`,
          return_url: `${currentUrl}/settings/payments?success=true`,
        });

        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error starting onboarding:', error);

        const errorData = error.response?.data;

        if (errorData?.error_type === 'stripe_not_configured') {
          this.$toast?.error(
            '⚙️ Stripe Not Configured\n\n'
            + 'Your platform administrator needs to set up Stripe API keys first.\n\n'
            + 'This is a one-time setup for the entire platform (not per-user).\n\n'
            + 'Next steps:\n'
            + '1. Platform admin creates Stripe account at stripe.com\n'
            + '2. Admin adds API keys to environment variables\n'
            + '3. Restart backend server\n'
            + '4. Then you can set up payment processing!',
            { timeout: 10000 },
          );
        } else if (error.response?.status === 403) {
          this.$toast?.error(
            'Permission Denied: Only organization administrators can set up payment processing. '
            + 'Please contact your organization admin.',
          );
        } else {
          this.$toast?.error(errorData?.error || 'Failed to start onboarding. Please try again.');
        }

        this.starting = false;
      }
    },

    async continueOnboarding() {
      this.continuing = true;
      try {
        const currentUrl = window.location.origin;
        const response = await api.post('/lms/connect/onboarding_link/', {
          refresh_url: `${currentUrl}/settings/payments`,
          return_url: `${currentUrl}/settings/payments?success=true`,
        });

        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error continuing onboarding:', error);
        this.$toast?.error('Failed to continue onboarding. Please try again.');
        this.continuing = false;
      }
    },

    manageAccount() {
      this.continueOnboarding();
    },

    formatRequirement(requirement) {
      return requirement
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
  },
};
</script>

<style scoped>
.payments-page {
  max-width: 960px;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.35rem;
}

.page-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.5;
  max-width: 42rem;
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: var(--p-ink-muted, #64748b);
}

.loading-state i {
  margin-right: 0.45rem;
  color: var(--p-accent, #1e3a5f);
}

.settings-card {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
}

.edge-card {
  border-left-width: 4px;
  padding-left: calc(1.35rem - 3px);
}

.edge-ok {
  border-left-color: #16a34a;
}

.edge-warn {
  border-left-color: #d97706;
}

.edge-neutral {
  border-left-color: var(--p-border-strong, #cbd5e1);
}

.card-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.card-header > i {
  color: var(--p-accent, #1e3a5f);
  font-size: 1.15rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.25rem;
}

.card-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-text {
  margin: 0;
  font-size: 0.88rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.5;
}

.info-text code {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.85em;
}

.info-text a {
  color: var(--p-accent, #1e3a5f);
  font-weight: 600;
}

.setup-steps {
  margin: 0;
  padding: 0 0 0 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.setup-steps li {
  font-size: 0.88rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.setup-steps strong {
  display: block;
  color: var(--p-ink, #1e293b);
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.setup-steps span {
  display: block;
}

.fee-block {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius-sm, 0.5rem);
  padding: 0.85rem 1rem;
  margin: 0;
}

.fee-title {
  margin: 0 0 0.35rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
}

.fee-desc {
  margin: 0 0 0.65rem;
  font-size: 0.82rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.metrics-list {
  margin: 0;
}

.fee-metrics {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius-sm, 0.5rem);
  padding: 0.35rem 0.85rem;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.metric-row:last-child {
  border-bottom: none;
}

.metric-row dt {
  font-size: 0.8rem;
  color: var(--p-ink-muted, #64748b);
  font-weight: 500;
}

.metric-row dd {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--p-ink, #1e293b);
  text-align: right;
}

.metric-row--ok dd {
  color: #166534;
}

.card-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  padding-top: 0.85rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  text-decoration: none;
  transition: background var(--p-transition, 180ms ease), border-color var(--p-transition, 180ms ease);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: var(--p-accent-contrast, #fff);
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover:not(:disabled) {
  background: var(--p-accent-hover, #162b46);
}

.btn-secondary {
  background: var(--p-surface-elevated, #fff);
  color: var(--p-ink, #1e293b);
  border-color: var(--p-border-strong, #cbd5e1);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--p-accent-subtle, #eef2f8);
}

.security-note {
  margin: 0;
  font-size: 0.82rem;
  color: var(--p-ink-muted, #64748b);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.security-note i {
  color: var(--p-success, #16a34a);
}

.usage-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.75rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.85rem;
  line-height: 1.45;
}

.usage-alert.warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.usage-alert.ok {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.usage-alert p {
  margin: 0.35rem 0 0;
}

.req-list {
  margin: 0.35rem 0 0;
  padding-left: 1.15rem;
}

.help-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}

.help-item {
  font-size: 0.85rem;
}

.help-label {
  margin: 0 0 0.25rem;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
}

.help-item p {
  margin: 0 0 0.35rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.text-link {
  color: var(--p-accent, #1e3a5f);
  font-weight: 600;
  text-decoration: none;
}

.text-link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .card-actions {
    flex-direction: column;
  }

  .card-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
